import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import SiteFooter from "@/components/SiteFooter";
import ThemeToggle from "@/components/ThemeToggle";
import GstToggle from "@/components/GstToggle";
import LangToggle from "@/components/LangToggle";
import SignupGate from "@/components/SignupGate";
import { useLang } from "@/context/LangContext";
import { nzTaxLink } from "@/lib/conversion";
import {
  LayoutDashboard,
  Calculator,
  Users,
  FileText,
  Receipt,
  Car,
  Gauge,
  Percent,
  Split,
  Wallet,
  Settings as SettingsIcon,
  Shield,
  LogOut,
  LogIn,
  Hammer,
  ArrowRight,
  ArrowLeft,
  Menu,
  X,
  ExternalLink,
} from "lucide-react";

// ---- mobile topbar helpers ----------------------------------------------
const PAGE_TITLES = {
  "/app/dashboard": "Dashboard",
  "/app/calc/gst": "GST",
  "/app/calc/quote": "Quote / Invoice",
  "/app/calc/margin": "Margin vs Markup",
  "/app/calc/expense": "Business vs Personal",
  "/app/calc/budget": "Budget Planner",
  "/app/calc/rideshare": "Is Uber Driving Worth It?",
  "/app/calc/mileage": "Mileage",
  "/app/calc/tax": "Tax / PAYE",
  "/app/clients": "Clients",
  "/app/jobs": "Saved Quotes",
  "/app/invoices": "Invoices",
  "/app/settings": "Settings",
  "/app/admin": "Admin",
  "/tools/gst": "GST",
  "/tools/quote": "Quote / Invoice",
  "/tools/margin": "Margin vs Markup",
  "/tools/expense": "Business vs Personal",
  "/tools/budget": "Budget Planner",
  "/tools/rideshare": "Is Uber Driving Worth It?",
  "/tools/mileage": "Mileage",
  "/tools/tax": "Tax / PAYE",
};

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (/^\/app\/invoices\/[^/]+$/.test(pathname)) return "Invoice";
  return "";
}

function getBackTarget(pathname, guestMode) {
  if (/^\/app\/invoices\/[^/]+$/.test(pathname))
    return { to: "/app/invoices", label: "Invoices" };
  if (
    pathname.startsWith("/app/") &&
    pathname !== "/app/dashboard" &&
    pathname !== "/app"
  ) {
    return { to: "/app/dashboard", label: "Dashboard" };
  }
  if (pathname.startsWith("/tools/")) {
    return { to: "/", label: "Home" };
  }
  return null;
}

const navItems = [
  { to: "dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "calc/gst", label: "GST", icon: Calculator, key: "gst" },
  { to: "calc/quote", label: "Quote / Invoice", icon: FileText, key: "quote" },
  { to: "calc/margin", label: "Margin vs Markup", icon: Percent, key: "margin" },
  { to: "calc/expense", label: "Business or Personal", icon: Split, key: "expense" },
  { to: "calc/budget", label: "Budget Planner", icon: Wallet, key: "budget" },
  { to: "calc/rideshare", label: "Is Uber Driving Worth It?", icon: Car, key: "rideshare", newTab: "/uber-worth-it" },
  { to: "calc/mileage", label: "Mileage", icon: Car, key: "mileage" },
  { to: "calc/tax", label: "Tax / PAYE", icon: Gauge, key: "tax" },
  { to: "clients", label: "Clients", icon: Users, key: "clients" },
  { to: "jobs", label: "Saved Quotes", icon: Hammer, key: "jobs" },
  { to: "invoices", label: "Invoices", icon: Receipt, key: "invoices" },
  { to: "settings", label: "Settings", icon: SettingsIcon, key: "settings" },
];

const guestItems = [
  { to: "gst", label: "GST", icon: Calculator, key: "gst" },
  { to: "quote", label: "Quote", icon: FileText, key: "quote" },
  { to: "margin", label: "Margin vs Markup", icon: Percent, key: "margin" },
  { to: "expense", label: "Business or Personal", icon: Split, key: "expense" },
  { to: "budget", label: "Budget Planner", icon: Wallet, key: "budget" },
  { to: "rideshare", label: "Is Uber Driving Worth It?", icon: Car, key: "rideshare", newTab: "/uber-worth-it" },
  { to: "mileage", label: "Mileage", icon: Car, key: "mileage" },
  { to: "tax", label: "Tax / PAYE", icon: Gauge, key: "tax" },
];

export default function AppLayout({ guestMode = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const items = guestMode ? guestItems : navItems;
  const { t } = useLang();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const SidebarBody = (
    <>
      <Link
        to="/"
        className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between"
        data-testid="sidebar-logo-link"
      >
        <Brand size="md" />
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="lg:hidden text-zinc-400 hover:text-white p-1"
          aria-label="Close menu"
          data-testid="sidebar-close-btn"
        >
          <X size={20} />
        </button>
      </Link>

      <nav className="flex-1 py-3 overflow-y-auto">
        {items.map((it) => {
          const Icon = it.icon;
          if (it.newTab) {
            // Opens the standalone shareable landing in a new tab —
            // useful when we want a URL that lives outside the app shell.
            return (
              <a
                key={it.key}
                href={it.newTab}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${it.key}`}
                className="flex items-center gap-3 px-5 py-3 border-l-2 border-transparent text-sm transition-colors text-zinc-400 hover:text-white hover:bg-[var(--surface-2)]"
              >
                <Icon size={16} strokeWidth={1.75} />
                <span className="font-mono uppercase tracking-wider text-xs">
                  {t(`nav.${it.key}`, it.label)}
                </span>
              </a>
            );
          }
          return (
            <NavLink
              key={it.key}
              to={it.to}
              data-testid={`nav-${it.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 border-l-2 text-sm transition-colors ${
                  isActive
                    ? "border-[var(--primary)] bg-[var(--surface-2)] text-[var(--primary)]"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-[var(--surface-2)]"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              <span className="font-mono uppercase tracking-wider text-xs">{t(`nav.${it.key}`, it.label)}</span>
            </NavLink>
          );
        })}
        {user?.role === "admin" && !guestMode && (
          <NavLink
            to="admin"
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-3 border-l-2 text-sm transition-colors ${
                isActive
                  ? "border-[var(--secondary)] bg-[var(--surface-2)] text-[var(--secondary)]"
                  : "border-transparent text-zinc-400 hover:text-white hover:bg-[var(--surface-2)]"
              }`
            }
          >
            <Shield size={16} strokeWidth={1.75} />
            <span className="font-mono uppercase tracking-wider text-xs">Admin</span>
          </NavLink>
        )}
      </nav>

      <div className="border-t border-[var(--border)] p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <a
            href={nzTaxLink("/", "sidebar-main-site")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-[11px] font-mono text-zinc-400 hover:text-[var(--primary)] min-w-0"
            data-testid="sidebar-main-site-link"
          >
            <span className="uppercase tracking-wider truncate">Main site ↗</span>
          </a>
          <div className="flex items-center gap-1.5 shrink-0">
            <LangToggle />
            <GstToggle />
            <ThemeToggle />
          </div>
        </div>
        {guestMode ? (
          <Link
            to="/login"
            data-testid="sidebar-login-btn"
            className="btn-primary w-full justify-center text-xs"
          >
            <LogIn size={14} /> Login to save
          </Link>
        ) : (
          <div className="space-y-3">
            <div className="text-xs font-mono">
              <div className="text-zinc-500 uppercase tracking-wider">Logged in</div>
              <div className="mt-1 truncate" data-testid="sidebar-user-email">
                {user?.email}
              </div>
              <div className="text-[var(--primary)] uppercase tracking-wider mt-0.5">
                {user?.role}
              </div>
            </div>
            <button
              onClick={doLogout}
              data-testid="sidebar-logout-btn"
              className="btn-ghost w-full justify-center text-xs"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen lg:flex bg-[var(--bg)] text-white">
      {/* MOBILE TOPBAR */}
      <header
        className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-3 py-2.5 bg-[var(--surface)] border-b border-[var(--border)] gap-2 no-print"
        data-testid="mobile-topbar"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {(() => {
            const back = getBackTarget(location.pathname, guestMode);
            if (back) {
              return (
                <button
                  type="button"
                  onClick={() => navigate(back.to)}
                  className="flex items-center gap-1 px-2 py-2 -ml-2 text-zinc-300 hover:text-[var(--primary)] active:text-[var(--primary)] shrink-0"
                  aria-label={`Back to ${back.label}`}
                  data-testid="mobile-back-btn"
                >
                  <ArrowLeft size={20} strokeWidth={2} />
                  <span className="font-mono text-xs uppercase tracking-wider">
                    {back.label}
                  </span>
                </button>
              );
            }
            return (
              <Link to="/" data-testid="mobile-logo-link" className="min-w-0 shrink-0">
                <Brand size="sm" variant="mark" />
              </Link>
            );
          })()}
          {(() => {
            const title = getPageTitle(location.pathname);
            if (!title) return null;
            return (
              <span
                className="font-mono text-[11px] uppercase tracking-wider text-[var(--primary)] truncate ml-1 border-l border-[var(--border)] pl-2"
                data-testid="mobile-page-title"
              >
                {title}
              </span>
            );
          })()}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <LangToggle />
          <GstToggle />
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="p-2 border border-[var(--border)] text-zinc-300 hover:text-[var(--primary)] hover:border-[var(--primary)]"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu size={18} />
          </button>
        </div>
      </header>

      {/* DESKTOP SIDEBAR */}
      <aside
        className="hidden lg:flex w-64 shrink-0 border-r border-[var(--border)] bg-[var(--surface)] flex-col no-print"
        data-testid="app-sidebar"
      >
        {SidebarBody}
      </aside>

      {/* MOBILE DRAWER */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex no-print" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative w-72 max-w-[85vw] bg-[var(--surface)] border-r border-[var(--border)] flex flex-col">
            {SidebarBody}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        {guestMode && <SignupGate guestMode={guestMode} />}
        {guestMode && <div className="hi-vis-stripe h-1.5 w-full no-print" />}
        {guestMode && (
          <div className="bg-[var(--surface)] border-b border-[var(--border)] px-4 md:px-8 py-3 flex items-center justify-between gap-3 flex-wrap no-print">
            <div className="flex items-center gap-2 text-xs font-mono text-zinc-400 min-w-0">
              <span className="chip chip-primary shrink-0">GUEST</span>
              <span className="truncate">{t("nav.calculators_only", "Calculators only — no saving")}</span>
            </div>
            <Link
              to="/register"
              className="btn-ghost text-xs shrink-0"
              data-testid="guest-upgrade-btn"
            >
              {t("nav.upgrade_free", "Upgrade free")} <ArrowRight size={14} />
            </Link>
          </div>
        )}
        <div className="p-4 md:p-8 max-w-[1400px] min-w-0" key={location.pathname}>
          {(() => {
            const isCalc = /\/calc\/|\/tools\//.test(location.pathname);
            // Budget Planner and Rideshare render their own GST controls
            // inline, so we suppress the global banner on those pages to
            // avoid duplication / conflicting toggles.
            const isBudget = /\/(calc|tools)\/budget(\/|$)/.test(location.pathname);
            const isRideshare = /\/(calc|tools)\/rideshare(\/|$)/.test(location.pathname);
            return isCalc && !isBudget && !isRideshare ? (
              <GstToggle variant="banner" className="mb-6" />
            ) : null;
          })()}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
